import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { DownloadService } from '@treasury/domain/download';
import { Entitlements, EntitlementsService } from '@treasury/domain/entitlements';
import { Feature } from '@treasury/domain/services/feature-flags';
import { StopPayment } from '@treasury/domain/stop-payments/stop-payment.entity';
import { StopPaymentsService } from '@treasury/domain/stop-payments/stop-payments.service';
import { TmContainer, TmFooterService } from '@treasury/presentation';
import { circlePlusIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-action-button';
import '@treasury/presentation/components/tm-blocking-loader';
import '@treasury/presentation/components/tm-download';
import { DownloadPageType } from '@treasury/presentation/components/tm-download.types';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { css, html, nothing, PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import { StopPaymentVm } from '../data/stop-payment.vm';
import '../partials/stop-payment-detail-sheet';
import '../partials/stop-payment-filter-sheet';
import '../partials/stop-payments-table';

export const tagName = 'stop-payment-activity-container';
@customElement(tagName)
export class StopPaymentActivityContainer extends TmContainer {
    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private stopPaymentsService: StopPaymentsService;

    @InjectProperty()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    declare private readonly downloadService: DownloadService<any>;

    @InjectProperty()
    declare private dialogService: TmDialogService;

    @InjectProperty()
    declare private readonly footerService: TmFooterService;

    @InjectProperty()
    declare private entitlementsService: EntitlementsService;

    @state()
    private stopPaymentVms: StopPaymentVm[] = [];

    @state()
    private selectedStopPayments: StopPaymentVm[] = [];

    @state()
    private stopPayment?: StopPaymentVm;

    @state()
    private detailSheetOpen = false;

    @state()
    private filterSheetOpen = false;

    @state()
    private filters: object = {};

    @state()
    private hasWorkflow = false;

    @state()
    private canApproveOrReject = false;

    @state()
    private tabs = [{ label: 'All Stop Payments', id: 'all-stop-payments' }];

    @state()
    private activeTab = 'all-stop-payments';

    public connectedCallback() {
        super.connectedCallback();
        this.headerService.configure({
            title: 'Stop Payments',
            menuItems: [
                {
                    title: 'Download',
                    icon: html`<tm-download
                        .page=${DownloadPageType.StopPayments}
                        .filterModel=${this.filters}
                        .disabled=${true}
                    ></tm-download>`,
                    action: () => undefined,
                },
            ],
        });
    }

    public disconnectedCallback() {
        super.disconnectedCallback?.();
        this.footerService.hide();
    }

    protected renderTable() {
        if (this.deviceService.isDesktop) {
            return html`<tm-section
                class=${classMap({
                    'flex flex-col': true,
                    'overflow-hidden h-full bg-page': !this.isDesktop,
                    'h-full mx-2 pb-2 rounded-lg': this.isDesktop,
                })}
            >
                <div
                    class=${classMap({
                        'flex justify-between items-center border-b': true,
                        'bg-primary': !this.isDesktop,
                    })}
                >
                    <tm-tabs
                        .tabs=${this.tabs}
                        .activeTab=${this.activeTab}
                        @switchTab=${(e: CustomEvent) => {
                            this.activeTab = e.detail.activeTab;
                        }}
                    ></tm-tabs>
                    ${this.isDesktop
                        ? html`<tm-button
                              importance="primary"
                              .icon=${circlePlusIcon}
                              class="mr-4"
                              @click=${() => {
                                  this.navService.navigate('/stop-payments/payment/create');
                              }}
                              >Add stop payment</tm-button
                          >`
                        : nothing}
                </div>
                <stop-payments-table
                    class="h-full"
                    .stopPaymentVms=${this.stopPaymentVms}
                    .loading=${this.loading}
                    @stop-payment-clicked=${(e: CustomEvent) => {
                        this.stopPayment = e.detail;
                        this.detailSheetOpen = true;
                    }}
                    @filter-stop-payments=${() => {
                        this.filterSheetOpen = true;
                    }}
                    @selection-changed=${(e: CustomEvent) => {
                        this.selectedStopPayments = e.detail;
                    }}
                    @stop-payment-canceled=${() => {
                        this.getAllStopPayments();
                    }}
                ></stop-payments-table
            ></tm-section>`;
        }
        return html`<stop-payments-table
            class="h-full"
            .loading=${this.loading}
            .stopPaymentVms=${this.stopPaymentVms}
            @stop-payment-clicked=${(e: CustomEvent) => {
                this.stopPayment = e.detail;
                this.detailSheetOpen = true;
            }}
            @filter-stop-payments=${() => {
                this.filterSheetOpen = true;
            }}
        ></stop-payments-table>`;
    }

    protected render() {
        return html` <tm-sidebar>
                <div
                    class=${classMap({
                        'flex-grow flex flex-col h-full': true,
                        'py-2': this.isDesktop,
                    })}
                    slot="main-content"
                >
                    <div class="flex-none">
                        ${this.hasWorkflow && !this.isDesktop
                            ? html`<tm-action-button
                                  class="mt-[8px] mb-[8px]"
                                  .buttonTitle=${'Add stop payment'}
                                  @click=${() => {
                                      this.navService.navigate(`stop-payments/payment/create`);
                                  }}
                              ></tm-action-button>`
                            : nothing}
                    </div>
                    <div class="flex-grow">${this.renderTable()}</div>
                </div>
                ${this.hasWorkflow
                    ? html` <div slot="sidebar-content">
                          <div class="px-2 py-2 bg-primary rounded-lg">
                              <div class="flex justify-center mb-2 font-medium text-header">
                                  Stop Payments
                              </div>
                              <div class="flex flex-col gap-2">
                                  <tm-action-button
                                      .buttonTitle=${'Add stop payment'}
                                      .buttonStyle=${'secondary'}
                                      @click=${() => {
                                          this.navService.navigate(`stop-payments/payment/create`);
                                      }}
                                  ></tm-action-button>
                              </div>
                          </div>
                      </div>`
                    : nothing}
            </tm-sidebar>

            <stop-payment-detail-sheet
                .open=${this.detailSheetOpen}
                .stopPayment=${this.stopPayment}
                @update-stop-payment=${() => {
                    this.getAllStopPayments();
                }}
                @close=${() => {
                    this.detailSheetOpen = false;
                }}
            ></stop-payment-detail-sheet>
            <stop-payment-filter-sheet
                .open=${this.filterSheetOpen}
                @apply-filters=${(e: CustomEvent) => {
                    this.filters = e.detail;
                    this.searchStopPayments();
                }}
                @set-filters=${(e: CustomEvent) => {
                    this.filters = e.detail;
                }}
                @close=${() => {
                    this.filterSheetOpen = false;
                }}
            ></stop-payment-filter-sheet
            >${this.deviceService.isDesktop && this.canApproveOrReject
                ? this.configureFooter()
                : nothing}`;
    }

    protected configureFooter() {
        if (!this.isDesktop) {
            this.footerService.hide();
            return;
        }
        this.footerService.configure({
            direction: 'row',
            buttonConfig: [
                {
                    text: 'Approve',
                    success: true,
                    disabled: !this.selectedStopPayments.length,
                    onClick: () => this.showApproveReject('approve'),
                },
                {
                    text: 'Reject',
                    error: true,
                    disabled: !this.selectedStopPayments.length,
                    onClick: () => this.showApproveReject('reject'),
                },
            ],
        });
    }

    protected firstUpdated() {
        this.checkFeatureFlag();
        this.getAllStopPayments();
    }

    protected updated(changedProps: PropertyValues) {
        if (changedProps.has('filters') || changedProps.has('stopPaymentVms')) {
            this.downloadService.updateConfig({
                filterModel: this.filters,
                isRecurring: false,
                disabled: !this.stopPaymentVms.length,
            });
        }
    }

    private async checkFeatureFlag() {
        const featureFlagService = await this.featureFlagService;
        this.hasWorkflow = await featureFlagService.isEnabled(Feature.PwaStopPaymentsWorkflow);
        this.canApproveOrReject = await this.entitlementsService.isEnabled(
            Entitlements.ApproveStopPayment
        );
    }

    private async getAllStopPayments() {
        await this.tryFetch(
            () => this.stopPaymentsService.getAllStopPayments(),
            stopPayments => {
                this.stopPaymentVms = stopPayments.map(payment => new StopPaymentVm(payment));
            },
            () => this.notificationService.renderError('Failed to load stop payments')
        );
    }

    private async searchStopPayments() {
        await this.tryFetch(
            () => this.stopPaymentsService.searchStopPayments(this.filters),
            stopPayments => {
                this.stopPaymentVms = stopPayments.map(
                    payment => new StopPaymentVm(payment as StopPayment)
                );
            },
            () => this.notificationService.renderError('Failed to load stop payments')
        );
    }

    private async showApproveReject(action: 'approve' | 'reject') {
        const actionText = action === 'approve' ? 'Approve' : 'Reject';
        const { reason } = await this.dialogService.open(
            html`<div>Are you sure you want to ${action} the selected stop payments?</div>
                <br /><tm-text-field
                    label="Comments"
                    @value-changed=${(e: CustomEvent) => {
                        const { value } = e.detail;
                        this.selectedStopPayments.forEach(payment => {
                            payment.remarks = [value];
                        });
                    }}
                ></tm-text-field>`,
            `${actionText} Stop Payments`,
            {
                buttons: {
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Confirm',
                    },

                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                    },
                },
            }
        );
        if (reason === 'confirm') {
            if (action === 'approve') {
                await this.tryFetch(
                    () =>
                        Promise.all([
                            this.stopPaymentsService.batchApproveStopPayments(
                                this.selectedStopPayments.map(p => p.id as number),
                                this.selectedStopPayments[0].remarks.join(' ')
                            ),
                        ]),
                    () => {
                        this.notificationService.renderSuccess(
                            `Stop payments ${action}d successfully`
                        );
                        this.selectedStopPayments = [];
                        this.getAllStopPayments();
                    },
                    () => this.notificationService.renderError(`Failed to ${action} stop payments`)
                );
            }
            if (action === 'reject') {
                await this.tryFetch(
                    () =>
                        Promise.all([
                            this.stopPaymentsService.batchRejectStopPayments(
                                this.selectedStopPayments.map(p => p.id as number),
                                this.selectedStopPayments[0].remarks.join(' ')
                            ),
                        ]),
                    () => {
                        this.notificationService.renderSuccess(
                            `Stop payments ${action}ed successfully`
                        );
                        this.selectedStopPayments = [];
                        this.getAllStopPayments();
                    },
                    () => this.notificationService.renderError(`Failed to ${action} stop payments`)
                );
            }
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    height: 100%;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: StopPaymentActivityContainer;
    }
}
